/**
 * Delivery shapes as pure target selection (PRD #784, slice C).
 *
 * A shape answers exactly one question: who is affected. It never applies a
 * payload, never mutates the world, never spawns an entity, and never reads a
 * presentation descriptor. `projectile` returns a spawn descriptor instead of
 * spawning, so world mutation stays with the runtime that owns it.
 *
 * Every shape takes a {@link FrozenAim}: a value record captured at cast start,
 * carrying the caster id, the caster origin, and the aimed point. There is no
 * live target entity in the signature by construction, so a resolution running
 * after a telegraph cannot re-aim at a target that has since moved. Occupancy is
 * supplied separately as {@link ShapeCandidate} snapshots, because occupancy is
 * read at resolution while aim is not.
 *
 * The sector and lane geometry here moved out of `dungeonCombat.ts` unchanged,
 * together with the Charge planner and its collision sampling, so the Warrior
 * migration in a later slice is a call-site change rather than a behavior
 * change.
 */

import { nearestDirectionForDelta, type Direction } from '../direction';
import type { EntityId } from '../entity';
import type { TilePoint } from '../mobile';
import {
  canOccupyDungeonPosition,
  DUNGEON_ENEMY_COLLISION_RADIUS_TILES,
  DUNGEON_ENEMY_COLLISION_SUBDIVISION_TILES,
  DUNGEON_PLAYER_COLLISION_RADIUS_TILES,
  dungeonCellFor,
  sweepDungeonDisplacement,
  type ContinuousPoint,
} from '../dungeonMovement';
import { canStepForLevel, walkableForLevel } from '../walkability';
import type { World } from '../world';
import type { AbilityShape } from './abilityCatalog';

/** Charge feel tuning. The ability definition owns distance, duration, and cooldown. */
export const DUNGEON_CHARGE_KNOCK_TILES = 1;
export const DUNGEON_CHARGE_COLLISION_SAMPLE_TILES = 0.125;
export const DUNGEON_CHARGE_CONTACT_RADIUS_TILES = 0.7;

/** Seconds of simulated time in one tick, used to size projectile lifetimes. */
const TICK_SECONDS = 0.05;

/** A frozen world position. `z` selects the map level the point stands on. */
export interface FrozenPosition {
  readonly x: number;
  readonly y: number;
  readonly z?: number;
}

/**
 * Aim captured at cast start and never re-read from the world.
 *
 * `point` is absent only for a cast with no direction, such as a self buff.
 * Directional shapes select nobody without it rather than inventing a facing.
 */
export interface FrozenAim {
  readonly casterId: EntityId;
  readonly origin: FrozenPosition;
  readonly point?: { readonly x: number; readonly y: number };
}

/**
 * A candidate target, snapshotted at resolution. This is a value record rather
 * than an entity so that no shape can reach live state or mutate a target.
 */
export interface ShapeCandidate {
  readonly id: EntityId;
  readonly position: FrozenPosition;
  /** Collision body radius. Defaults to the dungeon enemy radius. */
  readonly radiusTiles?: number;
}

/** What every shape returns: the selected combatants in stable entity-id order. */
export interface ShapeSelection {
  readonly targets: readonly EntityId[];
}

/** A projectile the runtime should spawn. The shape itself spawns nothing. */
export interface ProjectileSpawn {
  readonly origin: FrozenPosition;
  /** Direction from the caster to the aimed point, unnormalized. */
  readonly direction: { readonly x: number; readonly y: number };
  readonly speedTilesPerS: number;
  readonly rangeTiles: number;
  readonly ttlTicks: number;
}

export interface ProjectileSelection extends ShapeSelection {
  readonly spawn: ProjectileSpawn | null;
}

export interface DungeonChargeContact {
  target: EntityId;
  from: ContinuousPoint;
  to?: ContinuousPoint;
}

export interface DungeonChargePlan {
  facing: Direction;
  start: ContinuousPoint;
  end: ContinuousPoint;
  path: TilePoint[];
  contacts: DungeonChargeContact[];
}

/** The dash selection: a charge plan plus its contacted targets, id ordered. */
export interface DashSelection extends ShapeSelection, DungeonChargePlan {}

function squaredDistanceToSegment(
  pointX: number,
  pointY: number,
  endX: number,
  endY: number,
): number {
  const lengthSquared = endX * endX + endY * endY;
  if (lengthSquared === 0) {
    return pointX * pointX + pointY * pointY;
  }
  const t = Math.max(
    0,
    Math.min(1, (pointX * endX + pointY * endY) / lengthSquared),
  );
  const dx = pointX - endX * t;
  const dy = pointY - endY * t;
  return dx * dx + dy * dy;
}

/**
 * Test the enemy's full collision body against the exact circular attack sector.
 * The sector and dungeon collision circles use the same continuous world plane.
 */
export function dungeonMeleeSectorIntersectsCircle(
  target: ContinuousPoint,
  forward: ContinuousPoint,
  rangeTiles: number,
  arcDegrees: number,
  targetRadius = DUNGEON_ENEMY_COLLISION_RADIUS_TILES,
): boolean {
  if (
    !Number.isFinite(target.x) ||
    !Number.isFinite(target.y) ||
    !Number.isFinite(forward.x) ||
    !Number.isFinite(forward.y) ||
    !Number.isFinite(rangeTiles) ||
    !Number.isFinite(arcDegrees) ||
    !Number.isFinite(targetRadius) ||
    rangeTiles < 0 ||
    targetRadius < 0
  ) {
    return false;
  }
  const forwardLength = Math.hypot(forward.x, forward.y);
  if (forwardLength === 0) return false;
  const targetLength = Math.hypot(target.x, target.y);
  if (targetLength === 0) return true;

  const unitX = forward.x / forwardLength;
  const unitY = forward.y / forwardLength;
  const halfArc = Math.max(0, Math.min(Math.PI, (arcDegrees * Math.PI) / 360));
  if (halfArc === Math.PI) {
    return targetLength <= rangeTiles + targetRadius;
  }

  const cosine = (target.x * unitX + target.y * unitY) / targetLength;
  const cosineLimit = Math.cos(halfArc);
  const insideAngle = cosine >= cosineLimit;
  if (insideAngle && targetLength <= rangeTiles) return true;

  const perpendicularX = -unitY;
  const perpendicularY = unitX;
  const sideForward = Math.cos(halfArc) * rangeTiles;
  const sideLateral = Math.sin(halfArc) * rangeTiles;
  const leftX = unitX * sideForward + perpendicularX * sideLateral;
  const leftY = unitY * sideForward + perpendicularY * sideLateral;
  const rightX = unitX * sideForward - perpendicularX * sideLateral;
  const rightY = unitY * sideForward - perpendicularY * sideLateral;
  let distanceSquared = Math.min(
    squaredDistanceToSegment(target.x, target.y, leftX, leftY),
    squaredDistanceToSegment(target.x, target.y, rightX, rightY),
  );

  if (insideAngle) {
    const distanceToArc = Math.abs(targetLength - rangeTiles);
    distanceSquared = Math.min(
      distanceSquared,
      distanceToArc * distanceToArc,
    );
  }
  return distanceSquared <= targetRadius * targetRadius + 1e-12;
}

/**
 * Test a monster collision circle against Piercing Thrust's straight capsule.
 * `widthTiles` is the full width of the blade lane, while `rangeTiles` owns its
 * length from the Warrior's position.
 */
export function dungeonPierceIntersectsCircle(
  target: ContinuousPoint,
  forward: ContinuousPoint,
  rangeTiles: number,
  widthTiles: number,
  targetRadius = DUNGEON_ENEMY_COLLISION_RADIUS_TILES,
): boolean {
  if (
    !Number.isFinite(target.x) ||
    !Number.isFinite(target.y) ||
    !Number.isFinite(forward.x) ||
    !Number.isFinite(forward.y) ||
    !Number.isFinite(rangeTiles) ||
    !Number.isFinite(widthTiles) ||
    !Number.isFinite(targetRadius) ||
    rangeTiles < 0 ||
    widthTiles < 0 ||
    targetRadius < 0
  ) {
    return false;
  }
  const forwardLength = Math.hypot(forward.x, forward.y);
  if (forwardLength === 0) return false;
  const endX = (forward.x / forwardLength) * rangeTiles;
  const endY = (forward.y / forwardLength) * rangeTiles;
  const collisionRadius = widthTiles / 2 + targetRadius;
  return (
    squaredDistanceToSegment(target.x, target.y, endX, endY) <=
    collisionRadius * collisionRadius + 1e-12
  );
}

function cellFor(point: { x: number; y: number }): TilePoint {
  return dungeonCellFor(point);
}

function sameCell(a: TilePoint, b: TilePoint): boolean {
  return a.x === b.x && a.y === b.y;
}

function distanceToSegment(
  point: { x: number; y: number },
  start: { x: number; y: number },
  end: { x: number; y: number },
): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  const projection =
    lengthSquared === 0
      ? 0
      : Math.max(
          0,
          Math.min(
            1,
            ((point.x - start.x) * dx + (point.y - start.y) * dy) /
              lengthSquared,
          ),
        );
  return Math.hypot(
    point.x - (start.x + dx * projection),
    point.y - (start.y + dy * projection),
  );
}

/** Tuning a caller may override per cast. Omitted entries keep Charge's feel. */
export interface DungeonChargeTuning {
  /**
   * How close the dash line must pass a body to contact it. Comes from the
   * `dash` shape's `contactRadiusTiles`, so retuning the catalog entry moves
   * the hitbox.
   */
  contactRadiusTiles?: number;
  /**
   * How far a contacted body is shoved aside. Comes from the `knockback`
   * payload's `distanceTiles`, which the runtime passes in, because the shape
   * schema has no payload of its own to read it from.
   */
  knockbackTiles?: number;
}

/**
 * Resolve the straight cursor-bound Charge path and deterministic sideways shoves.
 * Pure and shared by authority and prediction so the local rush lands cleanly.
 */
export function planDungeonCharge(
  world: World,
  playerId: EntityId,
  monsterIds: readonly EntityId[],
  aim: { x: number; y: number },
  distanceTiles: number,
  predictedOrigin?: { x: number; y: number; z?: number },
  tuning: DungeonChargeTuning = {},
): DungeonChargePlan | null {
  const contactRadiusTiles =
    tuning.contactRadiusTiles ?? DUNGEON_CHARGE_CONTACT_RADIUS_TILES;
  const knockbackTiles = tuning.knockbackTiles ?? DUNGEON_CHARGE_KNOCK_TILES;
  const player = world.get(playerId);
  const position = predictedOrigin ?? player?.position;
  if (!position) return null;
  const facing = nearestDirectionForDelta(
    aim.x - position.x,
    aim.y - position.y,
  );
  if (facing === null) return null;
  const start = { x: position.x, y: position.y };
  const startCell = cellFor(start);
  const level = world.map.levelAt(startCell.x, startCell.y, position.z);
  const canStep = canStepForLevel(world, level);
  const path: TilePoint[] = [];
  const aimDx = aim.x - start.x;
  const aimDy = aim.y - start.y;
  const aimDistance = Math.hypot(aimDx, aimDy);
  const travelDistance = Math.min(Math.max(0, distanceTiles), aimDistance);
  const unitX = aimDx / aimDistance;
  const unitY = aimDy / aimDistance;
  const sampleCount = Math.max(
    1,
    Math.ceil(travelDistance / DUNGEON_CHARGE_COLLISION_SAMPLE_TILES),
  );
  let cursor = start;
  let previousCell = startCell;
  for (let sample = 1; sample <= sampleCount; sample += 1) {
    const distance = Math.min(
      travelDistance,
      sample * DUNGEON_CHARGE_COLLISION_SAMPLE_TILES,
    );
    const next = {
      x: start.x + unitX * distance,
      y: start.y + unitY * distance,
    };
    if (!canOccupyDungeonPosition(world, next, level)) break;
    const nextCell = cellFor(next);
    if (!sameCell(previousCell, nextCell)) {
      if (!canStep(previousCell, nextCell)) break;
      path.push(nextCell);
      previousCell = nextCell;
    }
    cursor = next;
  }

  const contacted = new Set<EntityId>();
  const pathCells = new Set(path.map((tile) => `${tile.x},${tile.y}`));
  const levelMonsters = monsterIds.flatMap((id) => {
    const monsterPosition = world.get(id)?.position;
    if (
      !monsterPosition ||
      world.map.levelAt(
        Math.round(monsterPosition.x),
        Math.round(monsterPosition.y),
        monsterPosition.z,
      ) !== level
    ) {
      return [];
    }
    return [[id, { x: monsterPosition.x, y: monsterPosition.y }] as const];
  });
  const occupied = new Set(
    levelMonsters.map(([, position]) => {
      const cell = cellFor(position);
      return `${cell.x},${cell.y}`;
    }),
  );
  const occupiedCircles = new Map(
    levelMonsters,
  );
  const contacts: DungeonChargeContact[] = [];
  let chargeLimitDistance = Math.hypot(cursor.x - start.x, cursor.y - start.y);
  const perpendicular = { x: -unitY, y: unitX };
  const perpendiculars = [
    {
      x: Math.round(perpendicular.x),
      y: Math.round(perpendicular.y),
    },
    {
      x: -Math.round(perpendicular.x),
      y: -Math.round(perpendicular.y),
    },
  ];
  const orderedMonsterIds = [...monsterIds].sort((left, right) => {
    const leftPosition = world.get(left)?.position;
    const rightPosition = world.get(right)?.position;
    const projection = (point: ContinuousPoint | undefined) =>
      point
        ? (point.x - start.x) * unitX + (point.y - start.y) * unitY
        : Number.POSITIVE_INFINITY;
    return projection(leftPosition) - projection(rightPosition);
  });
  for (const monsterId of orderedMonsterIds) {
    if (contacted.has(monsterId)) continue;
    const monsterPosition = world.get(monsterId)?.position;
    const projection = monsterPosition
      ? (monsterPosition.x - start.x) * unitX +
        (monsterPosition.y - start.y) * unitY
      : Number.POSITIVE_INFINITY;
    if (
      !monsterPosition ||
      projection < 0 ||
      projection > chargeLimitDistance + 1e-9 ||
      world.map.levelAt(
        Math.round(monsterPosition.x),
        Math.round(monsterPosition.y),
        monsterPosition.z,
      ) !== level ||
      distanceToSegment(monsterPosition, start, cursor) > contactRadiusTiles
    ) {
      continue;
    }
    contacted.add(monsterId);
    const from = { x: monsterPosition.x, y: monsterPosition.y };
    const fromCell = cellFor(from);
    occupied.delete(`${fromCell.x},${fromCell.y}`);
    occupiedCircles.delete(monsterId);
    const to = perpendiculars
      .filter((side) => side.x !== 0 || side.y !== 0)
      .map((side) => ({
        x: from.x + side.x * knockbackTiles,
        y: from.y + side.y * knockbackTiles,
      }))
      .find(
        (candidate) => {
          const candidateCell = cellFor(candidate);
          const swept = sweepDungeonDisplacement(
            world,
            from,
            {
              x: candidate.x - from.x,
              y: candidate.y - from.y,
            },
            {
              level,
              radius: DUNGEON_ENEMY_COLLISION_RADIUS_TILES,
              subdivision: DUNGEON_ENEMY_COLLISION_SUBDIVISION_TILES,
              slide: false,
              obstacles: [...occupiedCircles.values()].map((other) => ({
                ...other,
                radius: DUNGEON_ENEMY_COLLISION_RADIUS_TILES,
              })),
            },
          );
          return (
            canStep(fromCell, candidateCell) &&
            canOccupyDungeonPosition(
              world,
              candidate,
              level,
              DUNGEON_ENEMY_COLLISION_RADIUS_TILES,
            ) &&
            Math.hypot(
              swept.x - candidate.x,
              swept.y - candidate.y,
            ) < 1e-7 &&
            !occupied.has(`${candidateCell.x},${candidateCell.y}`) &&
            !pathCells.has(`${candidateCell.x},${candidateCell.y}`) &&
            [...occupiedCircles.values()].every(
              (other) =>
                Math.hypot(candidate.x - other.x, candidate.y - other.y) >=
                DUNGEON_ENEMY_COLLISION_RADIUS_TILES * 2 - 1e-9,
            )
          );
        },
      );
    if (to) {
      const toCell = cellFor(to);
      occupied.add(`${toCell.x},${toCell.y}`);
      occupiedCircles.set(monsterId, to);
    } else {
      occupied.add(`${fromCell.x},${fromCell.y}`);
      occupiedCircles.set(monsterId, from);
      chargeLimitDistance = Math.max(
        0,
        projection -
          DUNGEON_PLAYER_COLLISION_RADIUS_TILES -
          DUNGEON_ENEMY_COLLISION_RADIUS_TILES,
      );
      cursor = {
        x: start.x + unitX * chargeLimitDistance,
        y: start.y + unitY * chargeLimitDistance,
      };
      const limitCell = cellFor(cursor);
      const limitIndex = path.findIndex((cell) => sameCell(cell, limitCell));
      if (limitIndex >= 0) path.splice(limitIndex + 1);
      else path.length = 0;
    }
    contacts.push({ target: monsterId, from, ...(to ? { to } : {}) });
  }
  return { facing, start, end: cursor, path, contacts };
}

type ShapeOf<K extends AbilityShape['kind']> = Extract<AbilityShape, { kind: K }>;

/** Ascending entity id, the one stable order every shape returns targets in. */
function byEntityId(left: EntityId, right: EntityId): number {
  return left - right;
}

function stableTargets(ids: readonly EntityId[]): EntityId[] {
  return [...ids].sort(byEntityId);
}

/**
 * Level and z filtering, applied once here so no ability repeats it. A dungeon
 * level is identified by the z the entity stands at, which is how the dungeon
 * combat loop has always separated a roof plate from the floor below it.
 */
function onCasterLevel(aim: FrozenAim, candidate: ShapeCandidate): boolean {
  return (candidate.position.z ?? 0) === (aim.origin.z ?? 0);
}

function bodyRadius(candidate: ShapeCandidate): number {
  return candidate.radiusTiles ?? DUNGEON_ENEMY_COLLISION_RADIUS_TILES;
}

function forwardOf(aim: FrozenAim): ContinuousPoint | null {
  if (!aim.point) return null;
  return { x: aim.point.x - aim.origin.x, y: aim.point.y - aim.origin.y };
}

function offsetOf(aim: FrozenAim, candidate: ShapeCandidate): ContinuousPoint {
  return {
    x: candidate.position.x - aim.origin.x,
    y: candidate.position.y - aim.origin.y,
  };
}

function select(
  aim: FrozenAim,
  candidates: readonly ShapeCandidate[],
  hits: (candidate: ShapeCandidate) => boolean,
): ShapeSelection {
  return {
    targets: stableTargets(
      candidates
        .filter((candidate) => onCasterLevel(aim, candidate) && hits(candidate))
        .map((candidate) => candidate.id),
    ),
  };
}

/** `self`: the caster and nobody else, whatever the aim and the occupancy say. */
export function selectSelfTargets(
  _shape: ShapeOf<'self'>,
  aim: FrozenAim,
): ShapeSelection {
  return { targets: [aim.casterId] };
}

/** `cone`: the Crescent Sweep sector, measured against each collision body. */
export function selectConeTargets(
  shape: ShapeOf<'cone'>,
  aim: FrozenAim,
  candidates: readonly ShapeCandidate[],
): ShapeSelection {
  const forward = forwardOf(aim);
  if (!forward) return { targets: [] };
  return select(aim, candidates, (candidate) =>
    dungeonMeleeSectorIntersectsCircle(
      offsetOf(aim, candidate),
      forward,
      shape.rangeTiles,
      shape.arcDegrees,
      bodyRadius(candidate),
    ),
  );
}

/**
 * `circle`: everything within the radius of the caster. Whirlwind measures
 * centre to centre rather than body to body, and the chebyshev metric preserves
 * its square footprint exactly.
 */
export function selectCircleTargets(
  shape: ShapeOf<'circle'>,
  aim: FrozenAim,
  candidates: readonly ShapeCandidate[],
): ShapeSelection {
  return select(aim, candidates, (candidate) => {
    const offset = offsetOf(aim, candidate);
    const distance =
      shape.metric === 'chebyshev'
        ? Math.max(Math.abs(offset.x), Math.abs(offset.y))
        : Math.hypot(offset.x, offset.y);
    return distance <= shape.radiusTiles;
  });
}

/** `line`: the Piercing Thrust lane, measured against each collision body. */
export function selectLineTargets(
  shape: ShapeOf<'line'>,
  aim: FrozenAim,
  candidates: readonly ShapeCandidate[],
): ShapeSelection {
  const forward = forwardOf(aim);
  if (!forward) return { targets: [] };
  return select(aim, candidates, (candidate) =>
    dungeonPierceIntersectsCircle(
      offsetOf(aim, candidate),
      forward,
      shape.rangeTiles,
      shape.widthTiles,
      bodyRadius(candidate),
    ),
  );
}

/** `tile`: a disc centred on the aimed point rather than on the caster. */
export function selectTileTargets(
  shape: ShapeOf<'tile'>,
  aim: FrozenAim,
  candidates: readonly ShapeCandidate[],
): ShapeSelection {
  const point = aim.point;
  if (!point) return { targets: [] };
  return select(aim, candidates, (candidate) => {
    const distance = Math.hypot(
      candidate.position.x - point.x,
      candidate.position.y - point.y,
    );
    return distance <= shape.radiusTiles + bodyRadius(candidate);
  });
}

/**
 * `projectile`: nobody is selected at cast time. The shape hands back the spawn
 * the runtime should create, and the projectile's own flight decides who it
 * hits later.
 */
export function selectProjectileTargets(
  shape: ShapeOf<'projectile'>,
  aim: FrozenAim,
): ProjectileSelection {
  const direction = forwardOf(aim);
  if (!direction) return { targets: [], spawn: null };
  return {
    targets: [],
    spawn: {
      origin: aim.origin,
      direction,
      speedTilesPerS: shape.speedTilesPerS,
      rangeTiles: shape.rangeTiles,
      ttlTicks: Math.ceil(
        shape.rangeTiles / (shape.speedTilesPerS * TICK_SECONDS),
      ),
    },
  };
}

/**
 * The bodies a projectile's one-tick flight segment swept, nearest first.
 *
 * This is the projectile's own selection question, asked once per tick while it
 * flies rather than once at cast time, and it is here rather than in the runtime
 * for the reason every other selector is: geometry answers who is affected and
 * owns no state. The order is along travel, which is the order the arrow reached
 * the bodies, and it is what a pierce budget has to be spent in.
 *
 * A segment test rather than an endpoint test, so a fast shot cannot tunnel a
 * body between two ticks: at the reactable speed cap a projectile crosses more
 * than half a tile per tick, which is already wider than a body.
 */
export function selectProjectileSweepTargets(
  from: FrozenPosition,
  to: { readonly x: number; readonly y: number },
  candidates: readonly ShapeCandidate[],
): readonly EntityId[] {
  const level = from.z ?? 0;
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const lengthSquared = dx * dx + dy * dy;
  return candidates
    .flatMap((candidate) => {
      if ((candidate.position.z ?? 0) !== level) return [];
      const distance = distanceToSegment(candidate.position, from, to);
      if (distance > bodyRadius(candidate)) return [];
      const travel =
        lengthSquared === 0
          ? 0
          : ((candidate.position.x - from.x) * dx +
              (candidate.position.y - from.y) * dy) /
            lengthSquared;
      return [{ id: candidate.id, travel }];
    })
    // Ties break on entity id, so two bodies abreast of each other are struck in
    // the same stable order every other shape returns.
    .sort((left, right) =>
      left.travel === right.travel
        ? byEntityId(left.id, right.id)
        : left.travel - right.travel,
    )
    .map((contact) => contact.id);
}

/**
 * Whether a projectile may occupy this point, which is what stops an arrow at a
 * wall.
 *
 * A shot is a point rather than a body, so it asks the cell it is in and
 * nothing more: it clears a doorway a monster could not fit through, and it
 * cannot be wedged the way a swept collision circle can. The dungeon's own
 * body sweep is deliberately not reused here, because a zero-radius circle
 * overlaps nothing and would fly straight through the wall.
 */
export function projectileCanOccupy(
  world: World,
  point: { x: number; y: number },
  level: number,
): boolean {
  const walkable = walkableForLevel(world, level);
  return walkable(Math.round(point.x), Math.round(point.y));
}

/**
 * `dash`: the committed Charge path, its contacts, and its end position.
 *
 * The plan itself is {@link planDungeonCharge}, unchanged, because the dash has
 * to consult walkability and live occupancy to know where it stops. The shape
 * adds only the id-ordered target list; `contacts` keeps its own order, which is
 * the order along the path and is what the knockback resolution depends on.
 *
 * `candidates` is deliberately read as an id list here, and only here. A dash
 * has to sweep a path through live bodies to find where it is stopped and where
 * each shove lands, so it reads positions, levels, and collision from the world
 * rather than from the snapshot, and a snapshot that disagrees with the world
 * loses. That is not a licence to re-aim: aim stays frozen, and it is the
 * caster's committed line, not the target set, that counterplay depends on. The
 * body radius on a snapshot is likewise ignored, because the sweep uses the
 * dungeon collision radii the movement layer enforces.
 *
 * `tuning.knockbackTiles` carries the `knockback` payload's distance, which the
 * runtime owns; the shape defaults it to Charge's shipped value.
 */
export function selectDashTargets(
  world: World,
  shape: ShapeOf<'dash'>,
  aim: FrozenAim,
  candidates: readonly ShapeCandidate[],
  tuning: Pick<DungeonChargeTuning, 'knockbackTiles'> = {},
): DashSelection | null {
  if (!aim.point) return null;
  const plan = planDungeonCharge(
    world,
    aim.casterId,
    candidates.map((candidate) => candidate.id),
    aim.point,
    shape.distanceTiles,
    aim.origin,
    {
      contactRadiusTiles: shape.contactRadiusTiles,
      ...(tuning.knockbackTiles === undefined
        ? {}
        : { knockbackTiles: tuning.knockbackTiles }),
    },
  );
  if (!plan) return null;
  return {
    ...plan,
    targets: stableTargets(plan.contacts.map((contact) => contact.target)),
  };
}

export interface ShapeSelectionRequest {
  shape: AbilityShape;
  aim: FrozenAim;
  candidates: readonly ShapeCandidate[];
  /** Required by `dash` alone, which reads walkability to commit its path. */
  world?: World;
}

/**
 * The one entry point a runtime needs: dispatch a shape to its selector.
 * Returns `null` only when the shape cannot select at all, which today means a
 * dash with no world or no aim.
 */
export function selectShapeTargets(
  request: ShapeSelectionRequest,
): ShapeSelection | null {
  const { shape, aim, candidates, world } = request;
  switch (shape.kind) {
    case 'self':
      return selectSelfTargets(shape, aim);
    case 'cone':
      return selectConeTargets(shape, aim, candidates);
    case 'circle':
      return selectCircleTargets(shape, aim, candidates);
    case 'line':
      return selectLineTargets(shape, aim, candidates);
    case 'tile':
      return selectTileTargets(shape, aim, candidates);
    case 'projectile':
      return selectProjectileTargets(shape, aim);
    case 'dash':
      return world ? selectDashTargets(world, shape, aim, candidates) : null;
    default: {
      const exhaustive: never = shape;
      void exhaustive;
      return null;
    }
  }
}
